
import React, { useState } from 'react';
import Toast from './Toast';
import { API_BASE_URL } from '../config';

// Define the brand colors for easy reference
const BRAND_BLUE = '#004c8c'; // Example blue color
const BRAND_GOLD = '#FBBF24'; // Example gold color
const LIGHT_BG = '#F9FAFB'; // A light background color

const styles = {
    section: {
        paddingTop: '5rem', // py-20
        paddingBottom: '5rem', // py-20
        backgroundColor: 'white',
    },
    container: {
        maxWidth: '48rem', // max-w-4xl
        margin: '0 auto', // mx-auto
        paddingLeft: '1.5rem', // px-6
        paddingRight: '1.5rem', // px-6
    },
    heading: {
        fontSize: '2.25rem', // text-4xl
        fontWeight: '800', // font-extrabold
        textAlign: 'center', // text-center
        marginBottom: '1rem', // mb-4
        color: BRAND_BLUE,
    },
    paragraph: {
        textAlign: 'center', // text-center
        fontSize: '1.125rem', // text-lg
        color: '#4B5563', // text-gray-600
        marginBottom: '2.5rem', // mb-10
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5rem', // space-y-6
        backgroundColor: LIGHT_BG,
        padding: '2rem', // p-8 (md:p-10 is a bit complex for simple inline)
        borderRadius: '0.75rem', // rounded-xl
        boxShadow: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)', // shadow-inner
        border: '1px solid #E5E7EB', // border border-gray-200
    },
    label: {
        display: 'block',
        fontSize: '0.875rem', // text-sm
        fontWeight: '700', // font-bold
        marginBottom: '0.5rem', // mb-2
        color: '#374151', // text-gray-700
    },
    inputField: {
        width: '100%',
        border: '2px solid #D1D5DB', // border-2 border-gray-300
        borderRadius: '0.5rem', // rounded-lg
        padding: '0.75rem 1rem', // px-4 py-3
        fontSize: '1rem', // text-base
        outline: 'none',
        transition: 'border-color 0.15s ease-in-out',
    },
    inputFieldFocus: {
        borderColor: BRAND_GOLD, // focus:border-brand-gold (Note: This is hard to apply with pure inline styles; usually done with CSS)
    },
    textarea: {
        minHeight: '6rem', // equivalent to rows="4"
    },
    submitButtonContainer: {
        paddingTop: '1rem', // pt-4
    },
    submitButton: {
        width: '100%',
        fontSize: '1.125rem', // text-lg
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        
        // btn-primary style (example)
        backgroundColor: BRAND_GOLD,
        color: BRAND_BLUE,
        fontWeight: '600',
        padding: '0.75rem 1.5rem',
        borderRadius: '0.5rem',
        border: 'none',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease-in-out',
    },
    buttonHover: {
        backgroundColor: '#004c8c', // A slightly darker blue for hover effect
    }
};


function ContactForm() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: '',
    });
    const [isHovered, setIsHovered] = useState(false);
    const [toast, setToast] = useState({ visible: false, message: '', type: 'info' });

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    // Simple email validation
    const validateEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    // Function to call the API with form data
    const sendContactForm = async (data) => {
        try {
            // Map internal form keys to backend payload keys
            const payload = {
                contact_name: data.name,
                contact_email: data.email,
                contact_message: data.message,
            };

            // Replace the URL below with your actual API endpoint
            const response = await fetch(`${API_BASE_URL}/contact`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            return await response.json();
        } catch (error) {
            throw error;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // Validation
        if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
            setToast({ visible: true, message: 'All fields are required.', type: 'error' });
            return;
        }
        if (!validateEmail(formData.email)) {
            setToast({ visible: true, message: 'Please enter a valid email address.', type: 'error' });
            return;
        }
        try {
            await sendContactForm(formData);
            setToast({ visible: true, message: 'General Inquiry Sent! We will respond shortly.', type: 'success' });
            setFormData({ name: '', email: '', message: '' });
        } catch (error) {
            setToast({ visible: true, message: 'There was an error sending your inquiry. Please try again later.', type: 'error' });
            console.error('API Error:', error);
        }
    };

    // Hide toast after 3 seconds
    React.useEffect(() => {
        if (toast.visible) {
            const timer = setTimeout(() => setToast(t => ({ ...t, visible: false })), 3000);
            return () => clearTimeout(timer);
        }
    }, [toast.visible]);

    return (
        <section id="contact" style={styles.section}>
            <Toast visible={toast.visible} message={toast.message} type={toast.type} />
            <div style={styles.container}>
                <h2 style={styles.heading}>General Inquiry</h2>
                <p style={styles.paragraph}>
                    For non-urgent matters, employment inquiries, or general corporate information.
                </p>
                <form onSubmit={handleSubmit} style={styles.form}>
                    <div>
                        <label htmlFor="contact_name" style={styles.label}>Your Full Name*</label>
                        <input
                            type="text"
                            id="contact_name"
                            name="name"
                            required
                            placeholder="Full Name"
                            value={formData.name}
                            onChange={handleChange}
                            style={styles.inputField}
                        />
                    </div>
                    <div>
                        <label htmlFor="contact_email" style={styles.label}>Your Email Address*</label>
                        <input
                            type="email"
                            id="contact_email"
                            name="email"
                            required
                            placeholder="name@company.com"
                            value={formData.email}
                            onChange={handleChange}
                            style={styles.inputField}
                        />
                    </div>
                    <div>
                        <label htmlFor="contact_message" style={styles.label}>Description / Message*</label>
                        <textarea
                            id="contact_message"
                            name="message"
                            rows="4"
                            required
                            value={formData.message}
                            onChange={handleChange}
                            style={{ ...styles.inputField, ...styles.textarea }}
                            placeholder="Your question or message here..."
                        />
                    </div>
                    <div style={styles.submitButtonContainer}>
                        <button
                            type="submit"
                            style={{ ...styles.submitButton, ...(isHovered ? styles.buttonHover : {}) }}
                            onMouseEnter={() => setIsHovered(true)}
                            onMouseLeave={() => setIsHovered(false)}
                        >
                            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                SEND MESSAGE
                            </span>
                        </button>
                    </div>
                </form>
            </div>
        </section>
    );
}

export default ContactForm;